import { diasAte } from '@/shared/utils/dateUtils';

import type { CustasProcesso, CustasProcessoEstado } from '../types/custasProcessoTypes';
import type {
  CobrancaProcessoClienteApi,
  FinanceiroProcessoCliente,
} from '../types/financeiroProcessoClienteTypes';

const ESTADO_POR_STATUS: Partial<
  Record<CobrancaProcessoClienteApi['status'], CustasProcessoEstado>
> = {
  EM_ABERTO: 'EMITIDA',
  // Custa vencida no prazo adicional (C36): o processo segue vivo e o mesmo boleto
  // ainda paga. Colapsar isto em `VENCIDA` esconderia o botão de pagar. `EM_ATRASO`
  // não vem mais para a custa — só para o envio de AR, que não tem card aqui.
  EM_PRORROGACAO: 'EM_PRORROGACAO',
  PAGA: 'PAGA',
  VENCIDA: 'VENCIDA',
  ERRO_EMISSAO: 'ERRO_EMISSAO',
};

/**
 * Custas do processo, lidas da MESMA cobrança real que a aba Financeiro mostra —
 * nunca mais um valor de tabela fixa por rito, nem uma data de pagamento
 * calculada por fórmula (ClickUp 86akac3u9: total, status "Paga" e data de
 * pagamento fabricados no front divergiam da cobrança real, inclusive com data de
 * pagamento no futuro).
 *
 * `acoes` viaja intacta: o card não deriva botão de estado, pelo mesmo motivo que
 * a aba não deriva — o servidor é quem sabe o papel do usuário e a modalidade
 * congelada do processo.
 *
 * `null` sem cobrança de custas emitida ainda (pré-triagem) ou quando ela foi
 * cancelada/estornada — nada a cobrar, nenhum card a mostrar. `CANCELADA` e
 * `ESTORNADA` não têm equivalente em `CustasProcessoEstado`: uma custa cancelada
 * não bloqueia nada, o mesmo efeito de não ter cobrança nenhuma.
 */
export function derivarCustasDoFinanceiro(
  financeiro: Pick<FinanceiroProcessoCliente, 'modalidade'> | null,
  cobrancas: CobrancaProcessoClienteApi[],
): CustasProcesso | null {
  if (!financeiro || financeiro.modalidade === 'CONVENIADO') return null;
  const cobranca = cobrancas.find((c) => c.finalidade === 'CUSTAS_PROCESSO');
  if (!cobranca) return null;

  const estado = ESTADO_POR_STATUS[cobranca.status];
  if (!estado) return null;

  return {
    estado,
    valor: cobranca.valor,
    vencimentoEm: cobranca.vencimentoEm,
    // O tipo diz `string | null`, mas `fetchCobrancas` só faz cast: um core sem a C36 manda o
    // payload sem a chave, e `undefined` não pode virar "prazo adicional" em lugar nenhum.
    prazoFinalPagamentoEm: cobranca.prazoFinalPagamentoEm ?? null,
    diasRestantes: diasAte(cobranca.vencimentoEm),
    pagoEm: cobranca.pagoEm,
    linkPagamento: cobranca.linkPagamento,
    acoes: cobranca.acoes,
  };
}

/**
 * Estados em que a citação ainda depende do dinheiro entrar.
 *
 * `VENCIDA` entra porque o card e os banners continuam visíveis num processo
 * encerrado por falta de pagamento — o que mudou é o TEXTO e a AÇÃO, não a
 * visibilidade.
 */
const ESTADOS_QUE_BLOQUEIAM: readonly CustasProcessoEstado[] = [
  'EMITIDA',
  'EM_PRORROGACAO',
  'VENCIDA',
  'PROCESSANDO',
  'VERIFICANDO',
  'EMITINDO',
  'ERRO_EMISSAO',
];

export function custasBloqueiamCitacao(custas: CustasProcesso | null): boolean {
  return !!custas && ESTADOS_QUE_BLOQUEIAM.includes(custas.estado);
}

/**
 * Estados de origem sobre os quais o checkout aberto pinta `PROCESSANDO`/`VERIFICANDO`.
 *
 * São os dois em que o servidor oferece pagar o boleto: em aberto e o prazo adicional (C36).
 * Fora daqui a sobreposição é descartada — em especial, ela nunca produz `PAGA`, que só
 * existe se vier do servidor. Deixar o prazo adicional de fora fazia o "Pagar custas" abrir
 * o checkout sem a tela entrar em "Aguardando o pagamento" nem consultar o servidor.
 */
const ESTADOS_COM_CHECKOUT: readonly CustasProcessoEstado[] = ['EMITIDA', 'EM_PRORROGACAO'];

export function custasAceitamCheckout(custas: CustasProcesso | null): custas is CustasProcesso {
  return !!custas && ESTADOS_COM_CHECKOUT.includes(custas.estado);
}

/**
 * Custa no prazo adicional (C36), inclusive com o checkout aberto sobre ela.
 *
 * Card, banner e Pendências decidem por aqui, e não cada um do seu jeito: o selo de Pendências
 * lia só `estado` e voltava a "Aguardando pagamento" com o checkout aberto, enquanto o card
 * seguia no prazo adicional. A decisão sai do estado do servidor e nunca do
 * `prazoFinalPagamentoEm`: ler o campo fazia um payload sem a chave anunciar o encerramento do
 * processo numa custa em aberto, e o servidor pode mandar o prazo adicional sem o instante.
 */
export function custasNoPrazoAdicional(custas: CustasProcesso): boolean {
  return (custas.estadoAntesDoCheckout ?? custas.estado) === 'EM_PRORROGACAO';
}
